import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { store } from '../redux/store';
import { baseUrl } from './api';
import { endSession } from '../screens/auth/login/actions';
import { cacheKey, getCache, setCache } from './axiosCache';

/**
 * In-memory cache for API responses
 * Used to store and retrieve responses to avoid redundant requests
 * @type {Map<string, any>}
 */
const cache = new Map();

/**
 * Axios instance configured for the application
 * Includes base URL, timeout settings, and interceptors for authentication and caching
 * @type {import('axios').AxiosInstance}
 */
const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    // 'Cache-Control': 'no-cache, no-store, must-revalidate',
    // Pragma: 'no-cache',
    // Expires: '0',
    // 'Content-Type': 'application/x-www-form-urlencoded',
    // 'X-Device-OS': 'web',
  },
});

// Set default timeout for all requests
axiosInstance.defaults.timeout = 30000;
axiosInstance.defaults.timeoutErrorMessage = 'timeout';

/**
 * Request interceptor
 * Adds authentication token to requests and checks cache for existing responses
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    const tempConfig = { ...config };

    const { currentUser } = store.getState().auth || {};
    if (currentUser?.access_token) {
      tempConfig.headers.Authorization = `Bearer ${currentUser.access_token}`;
      const cachedResponse = getCache(cacheKey(tempConfig), cache);
      if (cachedResponse) {
        return Promise.reject({ cachedResponse });
      }
    }

    return tempConfig;
  },
  (error: any) => {
    Promise.reject(error);
  },
);

/**
 * Response interceptor
 * Caches successful responses and handles authentication errors
 */
axiosInstance.interceptors.response.use(
  async (response: AxiosResponse<any, any>) => {
    setCache(cacheKey(response?.config), response, cache);
    return response;
  },
  async (error: any) => {
    // Return cached response if available
    if (error?.cachedResponse) {
      return Promise.resolve(error?.cachedResponse);
    }
    // Handle authentication errors by ending the session
    if (error?.response?.status === 401) {
      await endSession();
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
